import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { AssistantSearchResult } from '@mews-commander/data-access';
import { Container, Stack, StackItem } from '@optimus-web/core';
import { AssistantFilterChips } from '../../assistantSearchTypeFilters';
import { AdditionalSearchType, AssistantSearchType, SearchType } from '../../assistantSearchTypes';
import { FilterStateProps } from '../../Assistant';
import { useActiveFilterChips } from './useAvailableFilterChips';
import { ResultFilterChipItem } from './ResultFilterChipItem';

export interface ResultsFilterChipProps {
    searchTypes: AssistantSearchType[];
    handleClick: (value: AssistantSearchType | AssistantFilterChips) => void;
    activeValue: AssistantSearchType;
    results?: AssistantSearchResult | null | undefined;
    selectedSearchType: AssistantSearchType;
    setFilterState: Dispatch<SetStateAction<FilterStateProps>>;
}

export const ResultsFilterChips = ({
    searchTypes,
    activeValue,
    handleClick,
    setFilterState,
    selectedSearchType,
    results,
}: ResultsFilterChipProps) => {
    const filteredSearchTypes = searchTypes.filter((type) => type !== 'Command');
    const allSearchTypes: AssistantSearchType[] = useMemo(
        () => [AdditionalSearchType.All, ...filteredSearchTypes],
        [filteredSearchTypes]
    );
    const [selectedChip, setSelectedChip] = useState<AssistantSearchType | AssistantFilterChips | undefined>(undefined);
    const [chipList, setChipList] = useState<(AssistantSearchType | AssistantFilterChips)[]>(allSearchTypes);
    const availableFilterChips = useActiveFilterChips(results);
    const getSecondaryChipList = useCallback(
        (firstLevelChip: SearchType) => {
            if (
                firstLevelChip === SearchType.Customer ||
                firstLevelChip === SearchType.Company ||
                firstLevelChip === SearchType.Bill ||
                firstLevelChip === SearchType.Reservation ||
                firstLevelChip === SearchType.Resource
            ) {
                return availableFilterChips[firstLevelChip];
            }
            return [];
        },
        [availableFilterChips]
    );

    const handleChipClick = useCallback(
        (value: AssistantSearchType | AssistantFilterChips, isActive: boolean) => {
            if (value in SearchType || value in AdditionalSearchType) {
                const newChipList: (AssistantSearchType | AssistantFilterChips)[] = getSecondaryChipList(
                    value as SearchType
                );
                // if the newChipList is empty, then we don't have second level filters
                if (newChipList.length === 0) {
                    setFilterState((prev) => ({
                        ...prev,
                        selectedSearchType: value as AssistantSearchType,
                        firstLevel: value as AssistantSearchType,
                        secondLevel: undefined,
                    }));
                    handleClick(value);
                }
                if (!isActive) {
                    newChipList.unshift(value as SearchType);
                    setChipList(newChipList);
                    setFilterState((prev) => ({
                        ...prev,
                        selectedSearchType: value as AssistantSearchType,
                        firstLevel: value as AssistantSearchType,
                        secondLevel: undefined,
                    }));
                } else {
                    setChipList(allSearchTypes);
                    setSelectedChip(AdditionalSearchType.All);
                    setFilterState((prev) => ({
                        ...prev,
                        firstLevel: AdditionalSearchType.All,
                        selectedSearchType: AdditionalSearchType.All,
                        secondLevel: undefined,
                    }));
                    handleClick(AdditionalSearchType.All);
                }
            } else if (!isActive) {
                const newChipList = [value];
                newChipList.unshift(selectedSearchType);
                setChipList(newChipList);
                setFilterState((prev) => ({
                    ...prev,
                    secondLevel: value as AssistantFilterChips,
                }));
                setSelectedChip(value);
            } else if (isActive) {
                setSelectedChip(undefined);
                const newChipList: (AssistantSearchType | AssistantFilterChips)[] = getSecondaryChipList(
                    selectedSearchType as SearchType
                );
                newChipList.unshift(selectedSearchType);
                setChipList(newChipList);
                setFilterState((prev) => ({
                    ...prev,
                    secondLevel: undefined,
                    firstLevel: selectedSearchType,
                }));
                handleClick(selectedSearchType);
            }
        },
        [handleClick, setFilterState, selectedSearchType, allSearchTypes, getSecondaryChipList]
    );

    return (
        <Container overflowX="auto" padding={['0', '0', '50', '200']}>
            <Stack spacing="100">
                {chipList.map((type) => {
                    const active = activeValue === type || type === selectedChip;
                    return (
                        <StackItem key={type}>
                            <ResultFilterChipItem active={active} handleChipClick={handleChipClick} type={type} />
                        </StackItem>
                    );
                })}
            </Stack>
        </Container>
    );
};
